import {
  ActionIcon, Badge, Button, Card, Group, Modal, Paper, Select, SimpleGrid, Stack, Switch, Table, Text, Textarea,
  TextInput, Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconLock, IconPencil, IconPlus, IconSearch, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { http } from "../../api/http";
import { PageHeader } from "../../components/ui";
import { segments, useT, type KeepReason, type ScriptTerm } from "../../i18n/script";
import AdminLayout from "./AdminLayout";

interface Term {
  id?: number; term: string; category: string; note?: string | null; case_sensitive: boolean; active: boolean;
  builtin?: boolean; created_by?: string | null; created_at?: string;
}
interface Resp { items: Term[]; categories: Record<string, string> }

const REASON: Record<KeepReason, string> = {
  izraz: "iz liste izraza", adresa: "internet adresa", "e-pošta": "e-adresa", putanja: "putanja",
  fajl: "naziv fajla", "ključ": "korisničko ime ili ključ", oznaka: "oznaka sa ciframa",
  "strana riječ": "strana riječ (q, w, x, y)", kod: "kod (JSON, heksadecimalni niz)",
};
const SAMPLE = "Copernicus Sentinel-2 L2A: NDVI za voćnjak u KO Kuljani (predmet AS-2026-00905), izvoz u GeoJSON i PDF. " +
  "Pošalji na referent@aaprs.org, fajl vocnjak_2026.las, EPSG:8678.";

/** Administracija liste izraza koji u ćirilici ostaju latinicom, sa provjerom uživo. */
export default function ScriptTermsPage() {
  const { t, reloadTerms, terms } = useT();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["script-terms-all"], queryFn: () => http.get<Resp>("/api/v1/script-terms/all") });
  const [edit, setEdit] = useState<Term | null>(null);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState<string | null>(null);
  const [state, setState] = useState<string>("sve");
  const [sample, setSample] = useState(SAMPLE);
  const cats = q.data?.categories ?? {};

  const done = async () => {
    await qc.invalidateQueries({ queryKey: ["script-terms-all"] });
    await reloadTerms();
  };
  const onError = (e: Error) => notifications.show({ color: "red", message: e.message });
  const save = useMutation({
    mutationFn: (d: Term) => (d.id ? http.put(`/api/v1/script-terms/${d.id}`, d) : http.post("/api/v1/script-terms", d)),
    onSuccess: async () => { setEdit(null); await done(); notifications.show({ color: "green", message: t("Lista izraza je sačuvana") }); },
    onError,
  });
  const cls = useQuery({ queryKey: ["script-codelists"],
    queryFn: () => http.get<{ code: string; name: string; keep_latin: boolean; items: number }[]>("/api/v1/script-terms/codelists") });
  const setCl = useMutation({ mutationFn: (d: { code: string; keep_latin: boolean }) => http.put(`/api/v1/script-terms/codelists/${d.code}`, d),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["script-codelists"] }); await reloadTerms(); }, onError });
  const toggle = useMutation({ mutationFn: (d: Term) => http.put(`/api/v1/script-terms/${d.id}`, { ...d, active: !d.active }), onSuccess: done, onError });
  const del = useMutation({ mutationFn: (id: number) => http.del(`/api/v1/script-terms/${id}`), onSuccess: done, onError });

  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    return (q.data?.items ?? []).filter((r) => (!s || r.term.toLowerCase().includes(s) || (r.note ?? "").toLowerCase().includes(s))
      && (!cat || r.category === cat)
      && (state === "sve" || (state === "aktivni" ? r.active : state === "iskljuceni" ? !r.active : state === "korisnicki" ? !r.builtin : true)));
  }, [q.data, search, cat, state]);

  // provjera uživo koristi i izraz koji se upravo unosi u prozoru
  const liveTerms: ScriptTerm[] = useMemo(() => {
    const original = edit?.id ? q.data?.items.find((r) => r.id === edit.id)?.term : undefined;
    const base = terms.filter((r) => r.term !== original);
    if (edit?.active && edit.term.trim()) base.push({ term: edit.term.trim(), case_sensitive: edit.case_sensitive });
    return base;
  }, [q.data, edit, terms]);
  const preview = (text: string) => (
    <Text size="sm" lh={1.7} data-no-translit>
      {segments(text, liveTerms).map((s, i) => s.kept
        ? <Tooltip key={i} label={t("Ostaje latinicom") + " — " + t(REASON[s.kept])} withArrow>
            <mark style={{ background: s.kept === "izraz" ? "var(--mantine-color-lime-1)" : "var(--mantine-color-blue-0)", color: "inherit", borderRadius: 4, padding: "0 2px" }}>{s.text}</mark>
          </Tooltip>
        : <span key={i}>{s.text}</span>)}
    </Text>
  );

  const total = q.data?.items.length ?? 0;
  const own = q.data?.items.filter((r) => !r.builtin).length ?? 0;
  return (
    <AdminLayout>
      <Stack>
        <PageHeader title="Izrazi bez transliteracije"
          description="Kada korisnik izabere ćirilicu, ovi izrazi ostaju latinicom: tehnički termini, nazivi servisa i proizvoda, formati fajlova, jedinice. Adrese, e-pošta, nazivi fajlova, oznake sa ciframa i strane riječi (sa slovima q, w, x, y) ostaju latinicom automatski."
          right={<Button leftSection={<IconPlus size={14} />} onClick={() => setEdit({ term: "", category: "ostalo", note: "", case_sensitive: true, active: true })}>{t("Novi izraz")}</Button>} />

        <Card withBorder radius="lg" padding="md">
          <Stack gap="xs">
            <Group justify="space-between">
              <Text fw={600} size="sm">{t("Provjera")}</Text>
              <Group gap={6}>
                <Badge variant="light" color="lime">{t("iz liste")}</Badge>
                <Badge variant="light" color="blue">{t("automatski")}</Badge>
              </Group>
            </Group>
            <Textarea autosize minRows={2} value={sample} onChange={(e) => setSample(e.currentTarget.value)}
              aria-label={t("Probni tekst")} />
            <Paper withBorder p="sm" radius="md" bg="var(--mantine-color-default-hover)">{preview(sample)}</Paper>
          </Stack>
        </Card>

        <Card withBorder radius="lg" padding="md">
          <Text fw={600} size="sm">{t("Šifarnici čiji nazivi ostaju latinicom")}</Text>
          <Text size="xs" c="dimmed" mb="sm">{t("Sorte, podloge, sredstva i đubriva pišu se latinicom i u ćiriličnom prikazu. Nove stavke tih šifarnika automatski ulaze u listu.")}</Text>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="xs">
            {(cls.data ?? []).map((c) => (
              <Switch key={c.code} size="sm" checked={c.keep_latin} label={`${t(c.name.split(" — ")[0])} (${c.items})`}
                onChange={(e) => setCl.mutate({ code: c.code, keep_latin: e.currentTarget.checked })} />
            ))}
          </SimpleGrid>
        </Card>

        <Group gap="sm" wrap="wrap">
          <TextInput leftSection={<IconSearch size={14} />} placeholder={t("Traži izraz ili napomenu")} value={search}
            onChange={(e) => setSearch(e.currentTarget.value)} w={260} />
          <Select placeholder={t("Sve kategorije")} clearable value={cat} onChange={setCat} w={220}
            data={Object.entries(cats).map(([value, label]) => ({ value, label: t(label) }))} />
          <Select value={state} onChange={(v) => setState(v ?? "sve")} w={180} allowDeselect={false}
            data={[{ value: "sve", label: t("Svi") }, { value: "aktivni", label: t("Aktivni") },
              { value: "iskljuceni", label: t("Isključeni") }, { value: "korisnicki", label: t("Dodali korisnici") }]} />
          <Text size="sm" c="dimmed" ml="auto">{t(`Ukupno ${total}, dodali korisnici ${own}`)}</Text>
        </Group>

        <Paper withBorder radius="lg" style={{ overflow: "auto" }}>
          <Table striped highlightOnHover verticalSpacing={6} fz="sm" miw={720}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("Izraz")}</Table.Th><Table.Th>{t("Kategorija")}</Table.Th><Table.Th>{t("Napomena")}</Table.Th>
                <Table.Th>{t("Velika/mala slova")}</Table.Th><Table.Th>{t("Aktivan")}</Table.Th><Table.Th w={90} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr key={r.id} style={{ opacity: r.active ? 1 : 0.55 }}>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <Text fw={600} size="sm" data-no-translit ff="monospace">{r.term}</Text>
                      {r.builtin && <Tooltip label={t("Ugrađeni izraz — može se isključiti, ali ne i obrisati")}><IconLock size={13} opacity={0.5} /></Tooltip>}
                    </Group>
                  </Table.Td>
                  <Table.Td>{t(cats[r.category] ?? r.category)}</Table.Td>
                  <Table.Td c="dimmed">{r.note || (r.builtin ? "" : r.created_by ? t("dodao/la") + " " + r.created_by : "")}</Table.Td>
                  <Table.Td>{r.case_sensitive ? t("tačno") : t("bilo koja")}</Table.Td>
                  <Table.Td><Switch size="xs" checked={r.active} onChange={() => toggle.mutate(r)} aria-label={t("Aktivan")} /></Table.Td>
                  <Table.Td>
                    <Group gap={2} wrap="nowrap">
                      <ActionIcon variant="subtle" onClick={() => setEdit({ ...r })} aria-label={t("Izmijeni")}><IconPencil size={15} /></ActionIcon>
                      {!r.builtin && (
                        <ActionIcon variant="subtle" color="red" aria-label={t("Obriši")} loading={del.isPending && del.variables === r.id}
                          onClick={() => r.id && del.mutate(r.id)}><IconTrash size={15} /></ActionIcon>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
              {!rows.length && (
                <Table.Tr><Table.Td colSpan={6}><Text c="dimmed" ta="center" py="md" size="sm">{q.isLoading ? t("Učitavanje…") : t("Nema izraza za izabrani filter")}</Text></Table.Td></Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Paper>

        {edit && (
          <Modal opened onClose={() => setEdit(null)} title={edit.id ? t("Izmjena izraza") : t("Novi izraz bez transliteracije")} size="lg">
            <Stack>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <TextInput label={t("Izraz")} description={t("Tačno kako se piše, npr. Copernicus ili NDVI")} data-autofocus
                  value={edit.term} disabled={edit.builtin} onChange={(e) => setEdit({ ...edit, term: e.currentTarget.value })} required />
                <Select label={t("Kategorija")} value={edit.category} disabled={edit.builtin} allowDeselect={false}
                  onChange={(v) => setEdit({ ...edit, category: v ?? "ostalo" })}
                  data={Object.entries(cats).map(([value, label]) => ({ value, label: t(label) }))} />
              </SimpleGrid>
              <TextInput label={t("Napomena")} value={edit.note ?? ""} onChange={(e) => setEdit({ ...edit, note: e.currentTarget.value })} />
              <Group>
                <Switch label={t("Poštuj velika i mala slova")} checked={edit.case_sensitive} disabled={edit.builtin}
                  onChange={(e) => setEdit({ ...edit, case_sensitive: e.currentTarget.checked })} />
                <Switch label={t("Aktivan")} checked={edit.active} onChange={(e) => setEdit({ ...edit, active: e.currentTarget.checked })} />
              </Group>
              {edit.term.trim() && (
                <Paper withBorder p="sm" radius="md">
                  <Text size="xs" c="dimmed" mb={4}>{t("Kako će izgledati u ćirilici")}</Text>
                  {preview(`${edit.term.trim()} — ${t("primjer rečenice")}: ${edit.term.trim()} se koristi u izvještaju.`)}
                </Paper>
              )}
              <Group justify="flex-end">
                <Button variant="default" onClick={() => setEdit(null)}>{t("Odustani")}</Button>
                <Button onClick={() => save.mutate(edit)} loading={save.isPending} disabled={!edit.term.trim()}>{t("Sačuvaj")}</Button>
              </Group>
            </Stack>
          </Modal>
        )}
      </Stack>
    </AdminLayout>
  );
}
